/**
 * String-typed drafts the borrower edits, and the conversions to/from the wire payloads in
 * `@tex/finance-shared`. Conversion throws `FormError` on the first problem; the API is still
 * the authority and re-validates every step.
 */
import {
  APPLICATION_STEPS,
  type ApplicationDraftSteps,
  type ApplicationStep,
  type ApplicationStepPayloads,
  type BusinessEntityType,
  type BuyerAddressView,
  type DiscrepancyKind,
  type FinancialFact,
  type FinancialFactSource,
  type FinancialPeriod,
  type MoneyView,
  MoneyError,
  parseDecimal,
} from '@tex/finance-shared'
import { draftKey, FormError } from './case-form'

export type WizardStep = ApplicationStep | 'review'
export const WIZARD_STEPS: readonly WizardStep[] = [...APPLICATION_STEPS, 'review']

export const STEP_LABELS: Record<ApplicationStep, string> = {
  business: 'Your business',
  owners: 'Owners',
  financials: 'Financing request',
  equipment_review: 'Confirm the equipment',
}

export const STEP_BLURBS: Record<ApplicationStep, string> = {
  business: 'Who is borrowing. Only you and Tex see these answers — the dealership does not.',
  owners: 'Anyone who owns a share of the business. No dates of birth or Social Security numbers are asked for here.',
  financials: 'How much you are asking for and what the business earns. "I don\u2019t know" is an acceptable answer.',
  equipment_review: 'Check the equipment and price the dealer entered against what you agreed.',
}

export const FINANCIAL_SOURCE_LABELS: Record<FinancialFactSource, string> = {
  stated: 'My best estimate',
  tax_return: 'Most recent tax return',
  financial_statement: 'Financial statement',
  bank_statements: 'Bank statements',
  accounting_software: 'Accounting software',
}

export const FINANCIAL_PERIOD_LABELS: Record<FinancialPeriod, string> = {
  trailing_12_months: 'Last 12 months',
  fiscal_year: 'Fiscal year',
  calendar_year: 'Calendar year',
  monthly: 'Per month',
}

export const DISCREPANCY_LABELS: Record<DiscrepancyKind, string> = {
  requested_amount: 'Amount requested',
  business_name: 'Business name',
  entity_type: 'Entity type',
  business_state: 'Business state',
  equipment_use_state: 'Where the equipment will be used',
  quote_disputed: 'Equipment or price',
}

// ── drafts ──

export interface AddressDraft {
  line1: string
  line2: string
  city: string
  state: string
  postal_code: string
}

export interface BusinessDraft {
  legal_name: string
  trading_name: string
  entity_type: BusinessEntityType | ''
  formed_on: string
  operating_since: string
  industry: string
  business_address: AddressDraft
  same_use_address: boolean
  equipment_use_address: AddressDraft
  representative: { name: string; title: string; email: string; phone: string }
}

export interface OwnerDraft {
  key: string
  full_name: string
  title: string
  ownership_percent: string
  email: string
  phone: string
  has_address: boolean
  address: AddressDraft
}

export interface FinancialFactDraft {
  known: boolean
  amount: string
  period: FinancialPeriod
  period_end: string
  source: FinancialFactSource
}

export interface FinancialsDraft {
  requested_amount: string
  intended_use: string
  repayment_source: string
  annual_revenue: FinancialFactDraft
  monthly_debt_service: FinancialFactDraft
}

export interface ItemFlagDraft {
  equipment_item_id: string
  note: string
}

export interface EquipmentReviewDraft {
  quote_matches: boolean | null
  note: string
  item_flags: ItemFlagDraft[]
}

export interface ApplicationDrafts {
  business: BusinessDraft
  owners: OwnerDraft[]
  financials: FinancialsDraft
  equipment_review: EquipmentReviewDraft
}

export const emptyAddress = (): AddressDraft => ({ line1: '', line2: '', city: '', state: '', postal_code: '' })

export const emptyOwner = (): OwnerDraft => ({
  key: draftKey(),
  full_name: '',
  title: '',
  ownership_percent: '',
  email: '',
  phone: '',
  has_address: false,
  address: emptyAddress(),
})

const emptyFact = (period: FinancialPeriod): FinancialFactDraft => ({ known: true, amount: '', period, period_end: '', source: 'stated' })

function addressDraftFrom(a: BuyerAddressView | null | undefined): AddressDraft {
  if (!a) return emptyAddress()
  return { line1: a.line1, line2: a.line2 ?? '', city: a.city, state: a.state, postal_code: a.postal_code }
}

function factDraftFrom(f: FinancialFact | undefined, period: FinancialPeriod): FinancialFactDraft {
  if (!f) return emptyFact(period)
  if (f.status === 'unknown') return { ...emptyFact(period), known: false }
  return { known: true, amount: (f.amount.amount_minor / 100).toFixed(2), period: f.period, period_end: f.period_end ?? '', source: f.source }
}

/**
 * Builds the editable drafts from what the server has. `prefill` is the dealer's lead data
 * the borrower may start from (business name, entity type, address, contact) — it is a
 * suggestion, never a saved answer, so a saved step always wins over it.
 */
export function draftsFrom(
  steps: ApplicationDraftSteps,
  prefill: { legal_name: string; entity_type: BusinessEntityType | null; representative_name: string; representative_email: string },
): ApplicationDrafts {
  const b = steps.business
  return {
    business: b
      ? {
          legal_name: b.legal_name,
          trading_name: b.trading_name ?? '',
          entity_type: b.entity_type,
          formed_on: b.formed_on,
          operating_since: b.operating_since ?? '',
          industry: b.industry,
          business_address: addressDraftFrom(b.business_address),
          same_use_address: b.equipment_use_address === null,
          equipment_use_address: addressDraftFrom(b.equipment_use_address),
          representative: { ...b.representative },
        }
      : {
          legal_name: prefill.legal_name,
          trading_name: '',
          entity_type: prefill.entity_type ?? '',
          formed_on: '',
          operating_since: '',
          industry: '',
          business_address: emptyAddress(),
          same_use_address: true,
          equipment_use_address: emptyAddress(),
          representative: { name: prefill.representative_name, title: '', email: prefill.representative_email, phone: '' },
        },
    owners: steps.owners
      ? steps.owners.owners.map((o) => ({
          key: draftKey(),
          full_name: o.full_name,
          title: o.title,
          ownership_percent: String(o.ownership_percent),
          email: o.email,
          phone: o.phone ?? '',
          has_address: o.address !== null,
          address: addressDraftFrom(o.address),
        }))
      : [emptyOwner()],
    financials: steps.financials
      ? {
          requested_amount: (steps.financials.requested_amount.amount_minor / 100).toFixed(2),
          intended_use: steps.financials.intended_use,
          repayment_source: steps.financials.repayment_source,
          annual_revenue: factDraftFrom(steps.financials.annual_revenue, 'trailing_12_months'),
          monthly_debt_service: factDraftFrom(steps.financials.monthly_debt_service, 'monthly'),
        }
      : {
          requested_amount: '',
          intended_use: '',
          repayment_source: '',
          annual_revenue: emptyFact('trailing_12_months'),
          monthly_debt_service: emptyFact('monthly'),
        },
    equipment_review: steps.equipment_review
      ? {
          quote_matches: steps.equipment_review.quote_matches,
          note: steps.equipment_review.note ?? '',
          item_flags: steps.equipment_review.item_flags.map((f) => ({ ...f })),
        }
      : { quote_matches: null, note: '', item_flags: [] },
  }
}

// ── draft → payload ──

const required = (v: string, field: string): string => {
  const t = v.trim()
  if (!t) throw new FormError(field, `${field} is required`)
  return t
}
const blankToNull = (s: string): string | null => (s.trim() ? s.trim() : null)

function toAddress(a: AddressDraft, field: string): BuyerAddressView {
  const out: BuyerAddressView = {
    line1: required(a.line1, `${field} street`),
    city: required(a.city, `${field} city`),
    state: required(a.state, `${field} state`),
    postal_code: required(a.postal_code, `${field} ZIP`),
  }
  if (!/^\d{5}(-\d{4})?$/.test(out.postal_code)) throw new FormError(`${field} ZIP`, `${field} ZIP: enter a 5-digit ZIP code`)
  if (a.line2.trim()) out.line2 = a.line2.trim()
  return out
}

function toMoney(raw: string, field: string): MoneyView {
  try {
    const m = parseDecimal(raw)
    return { amount_minor: m.amountMinor, currency: m.currency }
  } catch (err) {
    if (err instanceof MoneyError) throw new FormError(field, `${field}: enter a dollar amount like 12500.00`)
    throw err
  }
}

function toFact(d: FinancialFactDraft, field: string): FinancialFact {
  if (!d.known) return { status: 'unknown' }
  return { status: 'stated', amount: toMoney(d.amount, field), period: d.period, period_end: blankToNull(d.period_end), source: d.source }
}

export function toBusinessPayload(d: BusinessDraft): ApplicationStepPayloads['business'] {
  if (!d.entity_type) throw new FormError('Entity type', 'Entity type is required')
  return {
    legal_name: required(d.legal_name, 'Legal name'),
    trading_name: blankToNull(d.trading_name),
    entity_type: d.entity_type,
    formed_on: required(d.formed_on, 'Date formed'),
    operating_since: blankToNull(d.operating_since),
    industry: required(d.industry, 'Industry'),
    business_address: toAddress(d.business_address, 'Business address'),
    equipment_use_address: d.same_use_address ? null : toAddress(d.equipment_use_address, 'Equipment address'),
    representative: {
      name: required(d.representative.name, 'Your name'),
      title: required(d.representative.title, 'Your title'),
      email: required(d.representative.email, 'Your email'),
      phone: required(d.representative.phone, 'Your phone'),
    },
  }
}

export function toOwnersPayload(owners: OwnerDraft[]): ApplicationStepPayloads['owners'] {
  if (owners.length === 0) throw new FormError('Owners', 'List at least one owner')
  let total = 0
  const out = owners.map((o, i) => {
    const label = `Owner ${i + 1}`
    const pct = Number(o.ownership_percent)
    if (!o.ownership_percent.trim() || !Number.isFinite(pct) || pct <= 0 || pct > 100) {
      throw new FormError(`${label} ownership`, `${label}: ownership must be between 1 and 100 percent`)
    }
    total += pct
    return {
      full_name: required(o.full_name, `${label} name`),
      title: required(o.title, `${label} title`),
      ownership_percent: pct,
      email: required(o.email, `${label} email`),
      phone: blankToNull(o.phone),
      address: o.has_address ? toAddress(o.address, `${label} address`) : null,
    }
  })
  if (total > 100) throw new FormError('Owners', `Ownership adds up to ${total}%, which is more than 100%`)
  return { owners: out }
}

export function toFinancialsPayload(d: FinancialsDraft): ApplicationStepPayloads['financials'] {
  const requested = toMoney(d.requested_amount, 'Amount requested')
  if (requested.amount_minor <= 0) throw new FormError('Amount requested', 'Amount requested must be more than zero')
  return {
    requested_amount: requested,
    intended_use: required(d.intended_use, 'What the financing is for'),
    repayment_source: required(d.repayment_source, 'How it will be repaid'),
    annual_revenue: toFact(d.annual_revenue, 'Annual revenue'),
    monthly_debt_service: toFact(d.monthly_debt_service, 'Monthly debt payments'),
  }
}

export function toEquipmentReviewPayload(d: EquipmentReviewDraft): ApplicationStepPayloads['equipment_review'] {
  if (d.quote_matches === null) throw new FormError('Confirmation', 'Say whether the equipment and price match what you agreed')
  if (d.quote_matches) return { quote_matches: true, note: null, item_flags: [] }
  const flags = d.item_flags.filter((f) => f.note.trim()).map((f) => ({ equipment_item_id: f.equipment_item_id, note: f.note.trim() }))
  if (!d.quote_matches && !d.note.trim() && flags.length === 0) {
    throw new FormError('What differs', 'Tell us what does not match so Tex can sort it out with the dealer')
  }
  return { quote_matches: d.quote_matches, note: blankToNull(d.note), item_flags: flags }
}

export function toStepPayload<S extends ApplicationStep>(step: S, drafts: ApplicationDrafts): ApplicationStepPayloads[S] {
  const byStep: { [K in ApplicationStep]: () => ApplicationStepPayloads[K] } = {
    business: () => toBusinessPayload(drafts.business),
    owners: () => toOwnersPayload(drafts.owners),
    financials: () => toFinancialsPayload(drafts.financials),
    equipment_review: () => toEquipmentReviewPayload(drafts.equipment_review),
  }
  return byStep[step]()
}
